import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate, Outlet } from 'react-router-dom';
import { getAdminRole, getAdminName, logoutAdmin } from '../services/adminAuth';
import { LayoutDashboard, FileText, Building, Settings, LogOut, Users, Image, Briefcase } from 'lucide-react';

export default function AdminLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const [role, setRole] = useState('AGENT');
  const [adminName, setAdminName] = useState('');

  useEffect(() => {
    setRole(getAdminRole());
    setAdminName(getAdminName());
  }, [navigate]);

  const handleLogout = () => {
    logoutAdmin();
    navigate('/admin/login');
  };

  const menuItems = [
    { path: '/admin/dashboard', name: 'Dashboard', icon: LayoutDashboard },
    { path: '/admin/telegram', name: 'Telegram Members', icon: Users },
    { path: '/admin/contacts', name: 'CRM & Clients', icon: Briefcase },
    { path: '/admin/articles', name: 'Articles & Media', icon: FileText },
    { path: '/admin/real-estate', name: 'Real Estate', icon: Building },
    { path: '/admin/media', name: 'Media Library', icon: Image },
    { path: '/admin/settings', name: 'Settings', icon: Settings },
  ];

  const filteredMenu = menuItems.filter(
    (item) =>
      role === 'SUPER_ADMIN' ||
      item.name === 'Dashboard' ||
      item.name === 'Real Estate' ||
      item.name === 'Media Library' ||
      item.name === 'CRM & Clients'
  );

  return (
    <div className="min-h-screen bg-[#F9F9F9] text-[#333333] font-light lg:flex">
      <aside className="lg:sticky lg:top-0 lg:h-screen lg:w-72 shrink-0 bg-white border-r border-gray-200 flex flex-col shadow-sm">
        <div className="p-8 border-b border-gray-100">
          <h2 className="text-[#333333] font-bold text-2xl tracking-[0.2em] uppercase">Janus.</h2>
          {adminName && <p className="text-black font-medium mt-3">{adminName}</p>}
          <p className="text-gray-400 text-xs mt-1 tracking-widest font-bold uppercase">
            {role === 'SUPER_ADMIN' ? 'SUPER ADMIN' : 'B2B AGENT'}
          </p>
        </div>

        <nav className="flex-1 p-6 space-y-2 overflow-y-auto">
          {filteredMenu.map((item) => {
            const Icon = item.icon;
            const isActive =
              location.pathname === item.path || location.pathname.startsWith(`${item.path}/`);

            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center space-x-4 p-3 rounded-lg transition-all duration-300 ${
                  isActive
                    ? 'bg-gray-50 text-black border-l-2 border-black font-medium'
                    : 'text-gray-500 hover:bg-gray-50 hover:text-black'
                }`}
              >
                <Icon className="w-5 h-5 shrink-0" strokeWidth={isActive ? 2 : 1.5} />
                <span className="text-sm tracking-wide">{item.name}</span>
              </Link>
            );
          })}
        </nav>

        <div className="p-6 border-t border-gray-100 mt-auto bg-white">
          <button
            onClick={handleLogout}
            className="flex items-center space-x-4 p-3 w-full text-gray-500 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors text-sm"
          >
            <LogOut className="w-5 h-5 shrink-0" strokeWidth={1.5} />
            <span className="tracking-wide">Logout</span>
          </button>
        </div>
      </aside>

      <main className="flex-1 min-w-0 bg-[#F9F9F9]">
        <div className="p-6 md:p-8 xl:p-12 relative z-10 max-w-7xl mx-auto">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
